import { FontWeight } from '@hhgtech/hhg-components/mantine'
import { createStyles, keyframes, MantineColor, rem } from '@mantine/core'

import { CHART_DEMENSION_BY_SIZE } from '../constants'
import { ChartSizeType } from '../index.types'

type StyleParams = {
  color: MantineColor
  borderEndColorIndex: number
  borderStartColorIndex: number
  statusColorIndex: number
  valueColorIndex: number
  endCircleAngle: number
  gapAngleLeft: number
  circleAngle: number
  firstLoading: boolean
  size: ChartSizeType
  borderOutsideColor: string
  backgroundChartColor: string
}

const rotate = keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(360deg)' },
})

export default createStyles(
  (
    theme,
    {
      color,
      borderStartColorIndex,
      borderEndColorIndex,
      statusColorIndex,
      valueColorIndex,
      endCircleAngle,
      gapAngleLeft,
      circleAngle,
      firstLoading,
      size,
      borderOutsideColor,
      backgroundChartColor,
    }: StyleParams,
  ) => {
    const borderStartColor = theme.colors[color]?.[borderStartColorIndex]
    const borderEndColor = theme.colors[color]?.[borderEndColorIndex]
    const statusColor = theme.colors[color]?.[statusColorIndex]
    const valueColor = theme.colors[color]?.[valueColorIndex]

    const center = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '50%',
    }
    const width = CHART_DEMENSION_BY_SIZE[size]
    const borderSize = 6

    return {
      root: {
        position: 'relative',
        borderRadius: '50%',
        width,
        height: width,
        backgroundColor: borderOutsideColor,
        ...(firstLoading && {
          overflow: 'hidden',
        }),
      },
      border: {
        ...center,
        width,
        height: `${width}px`,
        position: 'relative',
        background: `conic-gradient(
            from 180deg,
            ${backgroundChartColor} 0deg ${gapAngleLeft}deg,
            ${borderOutsideColor} ${gapAngleLeft}deg,
            ${borderOutsideColor}  ${endCircleAngle}deg,
            ${backgroundChartColor} ${endCircleAngle}deg ${circleAngle}deg
          )`,
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          inset: 0,
          borderRadius: 'inherit',
          background: `conic-gradient(
            from 180deg,
            transparent 0deg ${gapAngleLeft}deg,
            ${borderStartColor} ${gapAngleLeft}deg,
            ${borderEndColor} var(--deg),
            transparent var(--deg) ${endCircleAngle}deg,
            transparent ${endCircleAngle}deg ${circleAngle}deg
          )`,
          ...(firstLoading && {
            animation: `${rotate} 2s linear infinite`,
            opacity: 0.5,
          }),
        },
      },

      borderWhite: {
        ...center,
        width: width - borderSize,
        height: width - borderSize,
        backgroundColor: '#fff',
        zIndex: 1,
      },
      background: {
        ...center,
        width: '100%',
        height: '100%',
        position: 'relative',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 6,
          left: 6,
          width: width - 18,
          height: width - 18,
          background: firstLoading
            ? theme.colors.neutral[0]
            : theme.colors[color]?.[0],
          opacity: 0.5,
          borderRadius: 'inherit',
        },
      },
      mainCircle: {
        ...center,
        position: 'relative',
        flexDirection: 'column',
        width: width - 68,
        height: width - 68,
        backgroundColor: '#fff',
        boxShadow: `5px 7px 20px ${valueColor}1F,
                    21px 30px 37px ${valueColor}1A,
                    48px 67px 50px ${valueColor}0F`,
        transition: 'all .3s ease',
        ...(firstLoading && {
          boxShadow: `5px 6px 8px ${theme.colors.gray[7]}1F,
                    8px 9px 10px ${theme.colors.gray[7]}1A,
                    10px 12px 14px ${theme.colors.gray[7]}0F`,
        }),
      },
      label: {},
      value: {
        fontSize: rem(size === 'sm' ? 56 : 64),
        lineHeight: rem(64),
        fontWeight: size === 'sm' ? FontWeight.halfBold : FontWeight.semiBold,
        color: valueColor,
        marginTop: -10,
      },
      status: {
        lineHeight: `${rem(20)} !important`,
        color: '#fff',
      },
      tagArea: {
        position: 'absolute',
        top: 'calc(100% - 26px)',
        left: 0,
        right: 0,
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
      },
      tag: {
        backgroundColor: statusColor,
        borderRadius: 20,
        padding: `${rem(size === 'sm' ? 3 : 4)} ${rem(16)} ${rem(
          size === 'sm' ? 5 : 4,
        )}`,

        minHeight: size === 'sm' ? 28 : 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all .3s ease',
        opacity: firstLoading ? 0 : 1,
      },
    }
  },
)
